(function () {
  // Length switching (length-selector.js) re-renders this whole section and
  // then re-runs every script inside it, including this file, so that the
  // bundle builder actually comes back to life for the new product instead
  // of staying dead until a full page reload. That means initBuilder() below
  // can now legitimately run more than once per page view. The handlers
  // bound straight onto `document` (as opposed to onto a specific element
  // that gets thrown away and recreated with the rest of the section) would
  // otherwise just stack up — each re-run adding ANOTHER copy on top of the
  // last — so every click, pack change, etc. would fire multiple times at
  // once. Stashing the currently-bound handler on window (survives a fresh
  // <script> execution, unlike a normal closure variable) lets each re-run
  // remove its predecessor before adding the new one, so there's only ever
  // one of each live at a time.
  window.__cpbGlobalListeners = window.__cpbGlobalListeners || {};

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    document.querySelectorAll('[data-custom-pack-builder]').forEach(initBuilder);
  });

  // Klaviyo's "Notify Me" back-in-stock button is built by cloning the
  // theme's real Add to Bag button, which means it inherits that button's
  // own attributes — including name="add" — so it was getting swept up by
  // the selectors below. That made this script overwrite "NOTIFY ME" with
  // pack-pricing text ("ADD TO BAG - £24.99") and, worse, apply the pack's
  // disabled/incomplete state to it too — so a shopper on a 3-pack with only
  // 1 selected couldn't click Notify Me at all. It has to be excluded
  // explicitly, the same way the sticky bar's copy of the button already is.
  function isKlaviyoNotifyButton(el) {
    return !!el.closest('#klaviyo-bis-button-container, [class*="kl-private-reset-css"], [data-a11y-identifier*="bis-button"]');
  }

  // Klaviyo clones whatever element it's replacing wholesale — attributes
  // and all — so a plain document.querySelector('[data-bdf-cta]') can just
  // as easily return Klaviyo's clone of the drawer-footer CTA as the real
  // one, depending on DOM order. That clone still carries data-bdf-cta, so
  // our incomplete-pack disabled/grey state was landing on it even though
  // getMainAtcButtons() (above) never touches it. Any lookup for an element
  // this script is going to WRITE to (disable, restyle, re-text) needs to
  // skip Klaviyo's copies the same way; pure reads (price/slot display,
  // which Klaviyo doesn't clone) don't need this.
  function queryOwn(selector) {
    const matches = document.querySelectorAll(selector);
    for (let i = 0; i < matches.length; i++) {
      if (!isKlaviyoNotifyButton(matches[i])) return matches[i];
    }
    return null;
  }

  function getMainAtcButtons() {
    return Array.from(document.querySelectorAll(
      '[data-testid="standalone-add-to-cart"], .add-to-cart-button, add-to-cart-component button, button[name="add"]'
    )).filter(btn => !btn.closest('sticky-add-to-cart') && !isKlaviyoNotifyButton(btn));
  }

  function getMainAtcContainers() {
    return Array.from(document.querySelectorAll('add-to-cart-component'))
      .filter(c => !c.closest('sticky-add-to-cart') && !isKlaviyoNotifyButton(c));
  }

  function initBuilder(root) {
    const grid = root.querySelector('[data-cpb-grid]');
    const countEl = root.querySelector('[data-cpb-count]');

    const isEnhancedPricingTemplate = root.dataset.templateSuffix === 'custom-product-version-3';
    const currencyCode = root.dataset.currency || 'USD';
    const localeCode = root.dataset.locale || 'en-GB';

    let priceFormatter;
    try {
      priceFormatter = new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency: currencyCode
      });
    } catch (e) {
      priceFormatter = { format: (v) => Number(v).toFixed(2) };
    }
    const fmt = (v) => priceFormatter.format(v);

    let pricingContainer = null;
    let currentPackKey = null;

    let quantity = 3;
    let activeSlotIndex = null;
    let selections = {};
    let currentSelectedSize = '';
    let currentCategoryHandle = '';
    let stepperQuantity = null;

    // ---- Denim (waist / length) support -------------------------------
    // Regular apparel uses one size option (L–6XL), rendered as the static
    // .drawer-size-btn row that already existed. Jeans carry two options
    // (waist + length), which is too many combinations to lay out as one
    // flat grid without it looking block-heavy — so for any category whose
    // products turn out to have a second option, the drawer swaps the flat
    // size row for a waist button row + a compact length dropdown, mirroring
    // the picker already used on the product page itself. Every other
    // category's flow (currentSelectedSize + the .drawer-size-btn handler
    // below) is completely untouched.
    let isDenimCategory = false;
    let currentSelectedWaist = '';
    let currentSelectedLength = '';
    let lastFetchedProducts = null;

    function normalizeVal(v) {
      return (v || '').toString().trim().toUpperCase();
    }

    function isRealProduct(product) {
      return !(product.title.includes('Pack') || product.title.includes('Set'));
    }

    // Big vs Tall — reads the data-product-fit attribute bundle-drawer.liquid
    // sets on #bundle-drawer (computed from the current product's tag).
    // Defaults to 'big' so nothing breaks before every product is tagged.
    function getCurrentProductFit() {
      const drawer = document.getElementById('bundle-drawer');
      return (drawer && drawer.dataset.productFit) || 'big';
    }

    function productMatchesFit(product, fit) {
      const tags = (product.tags || []).map(function (t) {
        return t.toString().trim().toLowerCase();
      });
      if (fit === 'tall') return tags.indexOf('tall') !== -1;
      // 'big' (default): show everything that isn't tagged Tall.
      return tags.indexOf('tall') === -1;
    }

    function detectDenimCategory(products) {
      return products.some(function (product) {
        if (!isRealProduct(product)) return false;
        return product.variants.some(function (v) {
          return v.option2 && v.option2.toString().trim() !== '';
        });
      });
    }

    function computeAvailableSizesInCat(products) {
      const set = new Set();
      products.forEach(function (product) {
        if (!isRealProduct(product)) return;
        product.variants.forEach(function (variant) {
          if (variant.available) {
            [variant.option1, variant.option2, variant.option3].forEach(function (opt) {
              if (opt) set.add(opt.toString().trim().toUpperCase());
            });
          }
        });
      });
      return set;
    }

    function collectDenimSizeData(products) {
      const waistSet = new Set();
      const lengthSet = new Set();
      const pairsAvailable = new Set();

      products.forEach(function (product) {
        if (!isRealProduct(product)) return;
        product.variants.forEach(function (variant) {
          const o1 = (variant.option1 || '').toString().trim();
          const o2 = (variant.option2 || '').toString().trim();
          if (!o1 || !o2) return;
          waistSet.add(o1);
          lengthSet.add(o2);
          if (variant.available) {
            pairsAvailable.add(o1.toUpperCase() + '|' + o2.toUpperCase());
          }
        });
      });

      const numSort = function (a, b) { return (parseFloat(a) || 0) - (parseFloat(b) || 0); };

      return {
        waists: Array.from(waistSet).sort(numSort),
        lengths: Array.from(lengthSet).sort(numSort),
        pairsAvailable: pairsAvailable
      };
    }

    function updateDenimSizeText() {
      const sizeTextEl = document.getElementById('drawer-selected-size-text');
      if (!sizeTextEl) return;
      if (currentSelectedWaist && currentSelectedLength) {
        sizeTextEl.textContent = currentSelectedWaist + 'W / ' + currentSelectedLength + 'L SELECTED';
      } else if (currentSelectedWaist) {
        sizeTextEl.textContent = currentSelectedWaist + 'W — select length';
      } else {
        sizeTextEl.textContent = 'Select waist';
      }
    }

    function updateLengthOptions(sizeData) {
      const lengthSelect = document.getElementById('drawer-length-select');
      if (!lengthSelect) return;
      Array.from(lengthSelect.options).forEach(function (opt) {
        if (!opt.value) return;
        if (!currentSelectedWaist) {
          opt.disabled = false;
          opt.textContent = opt.value;
          return;
        }
        const available = sizeData.pairsAvailable.has(
          currentSelectedWaist.toUpperCase() + '|' + opt.value.toUpperCase()
        );
        opt.disabled = !available;
        opt.textContent = available ? opt.value : opt.value + ' — Sold out';
      });
    }

    function renderDenimSizeControls(sizeData) {
      const waistList = document.getElementById('drawer-waist-list');
      const lengthSelect = document.getElementById('drawer-length-select');
      if (!waistList || !lengthSelect) return;

      waistList.innerHTML = '';
      sizeData.waists.forEach(function (w) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'drawer-waist-btn';
        btn.dataset.waist = w;
        btn.textContent = w;

        const hasAnyStock = sizeData.lengths.some(function (l) {
          return sizeData.pairsAvailable.has(w.toUpperCase() + '|' + l.toUpperCase());
        });
        if (!hasAnyStock) btn.classList.add('sold-out');
        if (w === currentSelectedWaist) btn.classList.add('active');

        btn.addEventListener('click', function () {
          if (btn.classList.contains('sold-out')) return;
          currentSelectedWaist = w;
          currentSelectedLength = '';
          waistList.querySelectorAll('.drawer-waist-btn').forEach(function (b) {
            b.classList.remove('active');
          });
          btn.classList.add('active');
          lengthSelect.value = '';
          updateLengthOptions(sizeData);
          updateDenimSizeText();
          renderDrawerProductCards(lastFetchedProducts);
        });

        waistList.appendChild(btn);
      });

      lengthSelect.innerHTML = '<option value="">Length</option>';
      sizeData.lengths.forEach(function (l) {
        const opt = document.createElement('option');
        opt.value = l;
        opt.textContent = l;
        lengthSelect.appendChild(opt);
      });
      lengthSelect.value = currentSelectedLength || '';

      lengthSelect.onchange = function () {
        currentSelectedLength = lengthSelect.value;
        updateDenimSizeText();
        renderDrawerProductCards(lastFetchedProducts);
      };

      updateLengthOptions(sizeData);
    }

    function toggleDenimMode(isDenim) {
      const standardList = document.getElementById('drawer-size-list-standard');
      const denimBlock = document.getElementById('drawer-denim-size');
      if (!standardList || !denimBlock) return;

      isDenimCategory = isDenim;

      if (isDenim) {
        standardList.hidden = true;
        denimBlock.hidden = false;
      } else {
        standardList.hidden = false;
        denimBlock.hidden = true;
        currentSelectedWaist = '';
        currentSelectedLength = '';
        const sizeTextEl = document.getElementById('drawer-selected-size-text');
        if (sizeTextEl && !currentSelectedSize) sizeTextEl.textContent = 'Select size';
      }
    }

    function denimVariantMatches(variant, waist, length) {
      const o1 = normalizeVal(variant.option1);
      const o2 = normalizeVal(variant.option2);
      const w = normalizeVal(waist);
      const l = normalizeVal(length);
      if (!w) return null;

      let waistSlot = null;
      if (o1 === w) waistSlot = 1;
      else if (o2 === w) waistSlot = 2;
      if (!waistSlot) return null;

      if (!l) return variant;

      const lengthVal = waistSlot === 1 ? o2 : o1;
      return lengthVal === l ? variant : null;
    }
    // ---------------------------------------------------------------------

    function findMainAtcButton() {
      const buttons = getMainAtcButtons();
      return buttons[0] || null;
    }

    function ensurePricingContainer() {
      if (!isEnhancedPricingTemplate) return null;
      if (pricingContainer && document.body.contains(pricingContainer)) return pricingContainer;

      const atcBtn = findMainAtcButton();
      if (!atcBtn) return null;

      const atcAnchor =
        atcBtn.closest('add-to-cart-component, buy-buttons-component, [data-testid="add-to-cart-button-wrapper"]') ||
        atcBtn;

      pricingContainer = document.createElement('div');
      pricingContainer.className = 'cpb-bundle-pricing';
      pricingContainer.hidden = true;
      pricingContainer.innerHTML = `
        <div class="cpb-bundle-pricing__prices">
          <span class="cpb-bundle-pricing__compare" data-cpb-compare hidden></span>
          <span class="cpb-bundle-pricing__sale" data-cpb-sale></span>
        </div>
        <div class="cpb-bundle-pricing__per-item">
          <span data-cpb-per-item></span>/item
        </div>
      `;

      atcAnchor.parentNode.insertBefore(pricingContainer, atcAnchor);
      return pricingContainer;
    }

    function detectCurrentPackKey() {
      if (!root.hidden) return 'custom_pack';
      const visibleGroup = document.querySelector('[data-preset-group]:not([hidden])');
      if (visibleGroup && visibleGroup.dataset.packKey) {
        return String(visibleGroup.dataset.packKey);
      }
      return '10';
    }

    function roundMoney(v) {
      return Math.round((v + Number.EPSILON) * 100) / 100;
    }

    // Matches Shopify's own automatic-discount engine (confirmed current
    // behaviour, rolled out store-wide as of Jan 2026): a percentage
    // discount is calculated PER UNIT and the discount amount is rounded
    // DOWN to the nearest penny — never rounded to the nearest penny, and
    // never rounded up. e.g. £24.99 at 40% off: discount = £9.996, floored
    // to £9.99 off, giving £15.00 — not the £14.99 a naive
    // round-the-final-price calculation would show. Working in whole pence
    // avoids floating-point drift on the multiply.
    function discountedUnitPrice(unitPrice, discountRate) {
      const priceCents = Math.round(unitPrice * 100);
      if (!discountRate) return priceCents / 100;
      const discountCents = Math.floor(priceCents * discountRate);
      return (priceCents - discountCents) / 100;
    }

    function getDiscountRateFor(qty) {
      if (qty === 3) return 0.40;
      if (qty === 6) return 0.50;
      if (qty >= 10) return 0.60;
      return 0;
    }

    function updatePricing() {
      if (!isEnhancedPricingTemplate) return;

      const container = ensurePricingContainer();
      if (!container) return;

      if (currentPackKey === 'singles') {
        container.hidden = true;
        return;
      }

      const p = computePricing();
      if (!p.valid) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      const compareEl = container.querySelector('[data-cpb-compare]');
      const saleEl = container.querySelector('[data-cpb-sale]');
      const perItemEl = container.querySelector('[data-cpb-per-item]');

      if (p.showCompare) {
        compareEl.hidden = false;
        compareEl.textContent = fmt(p.compare);
      } else {
        compareEl.hidden = true;
      }

      saleEl.textContent = fmt(p.sale);
      perItemEl.textContent = fmt(p.perItem);
    }

    function computePricing() {
      const basePrice = Number((window.currentProductData && window.currentProductData.price) || 0);
      if (basePrice <= 0) return { valid: false };

      // Whether the customer is actually using the slot-picker grid to build
      // this pack (true for every size — 3/6/10 — not just a literal
      // currentPackKey of 'custom_pack'). The pack-size selector often fires
      // pack:change with the SIZE as packKey ('3', '6', '10') rather than
      // 'custom_pack', since that string is also used as the display label —
      // so checking currentPackKey directly silently misses sized packs.
      // root.hidden is the reliable signal: it's only true when a completely
      // separate, non-slot-picking preset section is showing instead.
      const isUsingSlotPicker = !root.hidden;

      let packQuantity;
      if (currentPackKey === 'singles') {
        return { valid: false };
      } else if (isUsingSlotPicker) {
        packQuantity = quantity;
      } else {
        packQuantity = parseInt(currentPackKey, 10) || 0;
        if (packQuantity <= 0) return { valid: false };
      }

      const discountRate = getDiscountRateFor(packQuantity);

      let totalPrice = 0;
      let discountedTotal = 0;

      if (isUsingSlotPicker) {
        for (let i = 0; i < packQuantity; i++) {
          const unitPrice = (selections[i] && selections[i].price) ? Number(selections[i].price) : basePrice;
          totalPrice += unitPrice;
          discountedTotal += discountedUnitPrice(unitPrice, discountRate);
        }
      } else {
        totalPrice = basePrice * packQuantity;
        discountedTotal = discountedUnitPrice(basePrice, discountRate) * packQuantity;
      }

      if (discountedTotal <= 0) return { valid: false };

      return {
        valid: true,
        compare: totalPrice,
        sale: discountedTotal,
        perItem: discountedTotal / packQuantity,
        showCompare: discountRate > 0 && totalPrice > discountedTotal,
        packQuantity: packQuantity
      };
    }

    function observePresetGroups() {
      const presetGroups = document.querySelectorAll('[data-preset-group]');
      if (!presetGroups.length) return;

      const observer = new MutationObserver(() => {
        if (!root.hidden) return;
        const newKey = detectCurrentPackKey();
        if (newKey !== currentPackKey) {
          currentPackKey = newKey;
          updatePricing();
        }
      });

      presetGroups.forEach(group => {
        observer.observe(group, { attributes: true, attributeFilter: ['hidden'] });
      });
    }

    function autoSelectFirstProduct() {
      if (window.currentProductData && window.currentProductData.id) {
        const currentProd = window.currentProductData;
        selections[0] = {
          id: currentProd.id,
          title: currentProd.title,
          image: currentProd.image,
          size: currentProd.size || currentSelectedSize || '',
          price: parseFloat(currentProd.price || 0)
        };
      }
    }

    // Lets a shopper who just wants N of the product they're already on
    // skip manually filling every slot — one tap duplicates the current
    // item into whatever slots are still empty, leaving anything they've
    // already customised untouched. Slots stay empty by default (so the
    // Build Your Own mixing feature stays visible/discoverable) — this is
    // the deliberate, visible opt-in path for "no thanks, just this one".
    function fillRemainingSlotsWithCurrentProduct() {
      if (!window.currentProductData || !window.currentProductData.id) return;
      const currentProd = window.currentProductData;
      for (let i = 0; i < quantity; i++) {
        if (!selections[i]) {
          selections[i] = {
            id: currentProd.id,
            title: currentProd.title,
            image: currentProd.image,
            size: currentProd.size || currentSelectedSize || '',
            price: parseFloat(currentProd.price || 0)
          };
        }
      }
      renderSlots();
    }

    function renderQuickFillShortcut() {
      if (!grid || !grid.parentElement) return;
      let shortcut = grid.parentElement.querySelector('[data-cpb-quickfill]');

      const filled = Object.keys(selections).length;
      const hasEmptySlot = filled < quantity;
      const shouldShow = quantity > 1 && hasEmptySlot && !!(window.currentProductData && window.currentProductData.id);

      if (!shouldShow) {
        if (shortcut) shortcut.remove();
        return;
      }

      if (!shortcut) {
        shortcut = document.createElement('button');
        shortcut.type = 'button';
        shortcut.setAttribute('data-cpb-quickfill', '');
        shortcut.style.cssText = 'display:block; width:100%; text-align:center; margin-top:10px; background:none; border:none; padding:6px 0; font:inherit; font-size:12px; text-decoration:underline; text-underline-offset:2px; cursor:pointer; color:inherit; opacity:0.85;';
        shortcut.addEventListener('click', fillRemainingSlotsWithCurrentProduct);
        grid.insertAdjacentElement('afterend', shortcut);
      }

      shortcut.textContent = 'Want ' + quantity + ' of just this one? Add them all';
    }

    function setQuantity(newQuantity) {
      if (!newQuantity || isNaN(newQuantity)) return;
      newQuantity = parseInt(newQuantity, 10);

      if (quantity !== newQuantity) {
        quantity = newQuantity;

        // Keep whatever's already been picked rather than starting over —
        // growing the pack (e.g. 3 -> 6) just opens up new empty slots
        // alongside the ones already filled. Shrinking (e.g. 6 -> 3) can
        // only drop picks that no longer have a slot to sit in, so only
        // those get removed; everything that still fits stays exactly as
        // the customer had it. (Same fix as the standalone Bundle Builder
        // Page's pack-size selector.)
        Object.keys(selections).forEach(function (key) {
          if (Number(key) >= newQuantity) delete selections[key];
        });
      } else {
        quantity = newQuantity;
      }
      renderSlots();
    }

    function renderSlots() {
      if (!grid) return;
      grid.innerHTML = '';

      for (let i = 0; i < quantity; i++) {
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.className = 'cpb-slot';
        slot.dataset.index = i;

        const selected = selections[i];
        if (selected) {
          slot.classList.add('cpb-slot--filled');

          // A pencil (edit) icon rather than an × (remove) — the slot is
          // always swappable by tapping it, never has to be emptied first,
          // so the corner icon should read as "customise this" not
          // "delete this". Same action as tapping the slot itself; it's
          // here purely as a visible cue that a filled slot isn't final.
          const editBtn = document.createElement('span');
          editBtn.className = 'remove-item cpb-slot__edit';
          editBtn.setAttribute('aria-label', 'Change this item');
          editBtn.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>';
          editBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            activeSlotIndex = i;
            openBundleDrawer();
          });
          slot.appendChild(editBtn);

          const img = document.createElement('img');
          img.src = selected.image;
          img.alt = selected.title;
          slot.appendChild(img);

          const info = document.createElement('div');
          info.className = 'cpb-slot__info';

          if (selected.size) {
            const sizeEl = document.createElement('span');
            sizeEl.className = 'cpb-slot__size';
            sizeEl.textContent = selected.size;
            info.appendChild(sizeEl);
          }

          slot.appendChild(info);
        } else {
          const plus = document.createElement('span');
          plus.className = 'cpb-slot__plus';
          plus.textContent = '+';
          slot.appendChild(plus);
        }

        slot.addEventListener('click', function () {
          activeSlotIndex = i;
          openBundleDrawer();
        });

        grid.appendChild(slot);
      }
      updateCount();

      renderDrawerFooterSlots();
      renderDrawerHeaderBadge();
      updateDrawerFooterPricing();
      updateDrawerProductCardStates();
      renderQuickFillShortcut();
    }

    function getPackLabel() {
      if (currentPackKey === 'custom_pack') return 'CUSTOM PACK';
      if (currentPackKey === 'singles') return 'SINGLES';
      const q = parseInt(currentPackKey, 10);
      if (q > 0) return q + '-PACK';
      return (quantity || 0) + '-PACK';
    }

    function renderDrawerHeaderBadge() {
      const badge = document.querySelector('[data-bdf-progress]');
      if (!badge) return;
      if (!quantity || currentPackKey === 'singles') {
        badge.hidden = true;
        return;
      }
      const filled = Object.keys(selections).length;
      badge.textContent = getPackLabel() + ' — ' + filled + ' OF ' + quantity + ' CHOSEN';
      badge.hidden = false;
      badge.classList.toggle('is-complete', filled === quantity);
    }

    function renderDrawerFooterSlots() {
      const container = document.querySelector('[data-bdf-slots]');
      const footer = document.querySelector('[data-bdf-footer]');
      if (!container || !footer) return;

      if (!quantity || currentPackKey === 'singles') {
        footer.hidden = true;
        return;
      }
      footer.hidden = false;

      footer.setAttribute('data-slot-count', String(quantity));
      if (quantity >= 7) {
        footer.setAttribute('data-many-slots', '');
      } else {
        footer.removeAttribute('data-many-slots');
      }

      container.innerHTML = '';
      for (let i = 0; i < quantity; i++) {
        const slot = document.createElement('div');
        slot.className = 'bdf-slot';
        slot.dataset.index = i;

        const item = selections[i];
        if (item) {
          slot.classList.add('bdf-slot--filled');
          if (item.image) {
            const img = document.createElement('img');
            img.src = item.image;
            img.alt = item.title || '';
            slot.appendChild(img);
          }
          const rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'bdf-slot__remove';
          rm.setAttribute('aria-label', 'Remove item from bundle');
          rm.innerHTML = '&times;';
          rm.addEventListener('click', function (e) {
            e.stopPropagation();
            delete selections[i];
            renderSlots();
          });
          slot.appendChild(rm);
        } else {
          slot.textContent = '+';
        }
        container.appendChild(slot);
      }
    }

    function updateDrawerFooterPricing() {
      const compareEl = document.querySelector('[data-bdf-compare]');
      const saleEl = document.querySelector('[data-bdf-sale]');
      const perItemEl = document.querySelector('[data-bdf-per-item]');
      const cta = queryOwn('[data-bdf-cta]');
      if (!saleEl || !cta) return;

      if (currentPackKey === 'singles' || !quantity) {
        if (compareEl) { compareEl.hidden = true; compareEl.textContent = ''; }
        saleEl.textContent = '';
        if (perItemEl) perItemEl.textContent = '';
        cta.textContent = 'ADD PACK TO BAG';
        cta.disabled = true;
        cta.classList.add('is-disabled');
        return;
      }

      const p = computePricing();
      if (p.valid) {
        if (compareEl) {
          if (p.showCompare) {
            compareEl.hidden = false;
            compareEl.textContent = fmt(p.compare);
          } else {
            compareEl.hidden = true;
            compareEl.textContent = '';
          }
        }
        saleEl.textContent = fmt(p.sale);
        if (perItemEl) perItemEl.textContent = fmt(p.perItem) + ' / ITEM';
      } else {
        if (compareEl) { compareEl.hidden = true; compareEl.textContent = ''; }
        saleEl.textContent = '';
        if (perItemEl) perItemEl.textContent = '';
      }

      const filledCount = Object.keys(selections).length;
      // See the isUsingSlotPicker comment in computePricing() — same reason
      // this can't key off currentPackKey === 'custom_pack' directly.
      const isCustomPackIncomplete = !root.hidden && filledCount < quantity;

      if (isCustomPackIncomplete) {
        cta.textContent = 'SELECT ' + (quantity - filledCount) + ' MORE';
        cta.disabled = true;
        cta.classList.add('is-disabled');
        return;
      }

      const stepperMismatch =
        currentPackKey !== 'singles' &&
        stepperQuantity !== null &&
        Number(stepperQuantity) < Number(quantity);

      if (stepperMismatch) {
        cta.textContent = 'ADD PACK TO BAG';
        cta.disabled = true;
        cta.classList.add('is-disabled');
        return;
      }

      cta.textContent = 'ADD PACK TO BAG';
      cta.disabled = false;
      cta.classList.remove('is-disabled');
    }

    function updateDrawerProductCardStates() {
      const cards = document.querySelectorAll('#drawer-products-container .drawer-product-card');
      if (!cards.length) return;
      cards.forEach(function (card) {
        const pid = card.dataset.productId;
        const btn = card.querySelector('.drawer-add-btn');
        if (!btn || !pid) return;
        if (btn.classList.contains('disabled') || btn.disabled) return;

        let count = 0;
        Object.keys(selections).forEach(function (k) {
          if (String(selections[k].productId) === String(pid)) count++;
        });
        if (count > 0) {
          btn.textContent = 'IN BUNDLE — ' + count;
          btn.classList.add('in-bundle');
        } else {
          btn.textContent = 'ADD TO BUNDLE';
          btn.classList.remove('in-bundle');
        }
      });
    }

    function findNextEmptySlot() {
      for (let i = 0; i < quantity; i++) {
        if (!selections[i]) return i;
      }
      return -1;
    }

    function getEffectiveQuantity() {
      return Math.max(quantity, stepperQuantity !== null ? Number(stepperQuantity) : quantity);
    }

    function updateCount() {
      if (!countEl) return;
      const filled = Object.keys(selections).length;
      countEl.textContent = filled + ' / ' + quantity;

      const stepperMismatch =
        currentPackKey !== 'singles' &&
        stepperQuantity !== null &&
        Number(stepperQuantity) < Number(quantity);

      // A Build Your Own Bundle isn't ready to add to the bag until every
      // slot has something in it — otherwise the main Add to Bag click
      // handler below silently fills any empty slot with another of the
      // base product, so a shopper could tap "Add to Bag" having chosen only
      // 1 of 3 items and get charged for 3 of the one they picked. This
      // applies to every size built this way (3/6/10), so it keys off
      // whether the slot-picker grid itself is in use (!root.hidden), not
      // the string value of currentPackKey — the pack-size selector often
      // sets currentPackKey to the size ('3'/'6'/'10') rather than the
      // literal string 'custom_pack', which used to make this check silently
      // skip every sized pack.
      const isCustomPackIncomplete = !root.hidden && filled < quantity;

      const canAddToCart =
        !!(window.currentProductData && window.currentProductData.id) &&
        !stepperMismatch &&
        !isCustomPackIncomplete;

      const atcButtons = getMainAtcButtons();

      atcButtons.forEach(btn => {
        const btnText =
          btn.querySelector('.add-to-cart-text__content span span') ||
          btn.querySelector('.add-to-cart-text') ||
          btn;

        if (isCustomPackIncomplete) {
          btnText.textContent = 'SELECT ' + (quantity - filled) + ' MORE';
        } else if (isEnhancedPricingTemplate) {
          btnText.textContent = 'ADD TO BAG';
        } else {
          const discountRate = getDiscountRateFor(quantity);
          const basePrice = window.currentProductData ? Number(window.currentProductData.price || 0) : 0;
          const effectiveQuantity = getEffectiveQuantity();
          let discountedPrice = 0;
          for (let i = 0; i < effectiveQuantity; i++) {
            const item = selections[i];
            const unitPrice = (item && item.price) ? Number(item.price) : basePrice;
            if (unitPrice > 0) {
              discountedPrice += discountedUnitPrice(unitPrice, discountRate);
            }
          }

          if (canAddToCart && discountedPrice > 0) {
            const formattedPrice = fmt(discountedPrice);
            btnText.textContent = `ADD TO BAG - ${formattedPrice}`;
          } else {
            btnText.textContent = 'ADD TO BAG';
          }
        }
      });

      if (isEnhancedPricingTemplate && !root.hidden) {
        updatePricing();
      }

      toggleAddToCartButton(canAddToCart);
    }


    function toggleAddToCartButton(isFull) {
      const atcButtons = getMainAtcButtons();
      const atcContainers = getMainAtcContainers();

      if (isFull) {
        document.body.classList.add('bundle-is-complete');

        atcButtons.forEach(btn => {
          btn.removeAttribute('disabled');
          btn.disabled = false;
          btn.classList.remove('disabled', 'atc-disabled');
          btn.style.setProperty('opacity', '1', 'important');
          btn.style.setProperty('pointer-events', 'auto', 'important');
          btn.style.setProperty('cursor', 'pointer', 'important');
          btn.style.setProperty('filter', 'none', 'important');
        });

        atcContainers.forEach(container => {
          container.removeAttribute('disabled');
          container.style.setProperty('pointer-events', 'auto', 'important');
          container.style.setProperty('opacity', '1', 'important');
        });

      } else {
        document.body.classList.remove('bundle-is-complete');

        atcButtons.forEach(btn => {
          btn.setAttribute('disabled', 'true');
          btn.disabled = true;
          btn.classList.add('atc-disabled');
          btn.style.setProperty('opacity', '0.4', 'important');
          btn.style.setProperty('pointer-events', 'none', 'important');
          btn.style.setProperty('cursor', 'not-allowed', 'important');
        });

        atcContainers.forEach(container => {
          container.setAttribute('disabled', 'true');
          container.style.setProperty('pointer-events', 'none', 'important');
          container.style.setProperty('opacity', '0.4', 'important');
        });
      }
    }

    function openBundleDrawer() {
      const drawer = document.getElementById('bundle-drawer');
      const overlay = document.getElementById('bundle-drawer-overlay');
      if (drawer && overlay) {
        drawer.classList.add('active');
        overlay.classList.add('active');
      }

      renderDrawerHeaderBadge();
      renderDrawerFooterSlots();
      updateDrawerFooterPricing();

      if (currentCategoryHandle) {
        fetchProductsForDrawer();
      } else {
        const container = document.getElementById('drawer-products-container');
        if (container) {
          container.innerHTML = '<p class="drawer-loading-text">Please select a category.</p>';
        }
      }
    }

    function closeBundleDrawer() {
      const drawer = document.getElementById('bundle-drawer');
      const overlay = document.getElementById('bundle-drawer-overlay');
      if (drawer) drawer.classList.remove('active');
      if (overlay) overlay.classList.remove('active');
    }

    // Splats the section HTML Shopify's cart endpoints return back into the
    // page, and fires the same events the rest of the theme listens for.
    // Pulled out so both the "added successfully" path and the "something
    // went wrong" path below can share it.
    function applyCartSections(cartData) {
      if (cartData.sections) {
        Object.keys(cartData.sections).forEach(secId => {
          const html = cartData.sections[secId];
          if (html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const currentEl = document.getElementById(`shopify-section-${secId}`) || document.querySelector(secId);
            const newEl = parsed.getElementById(`shopify-section-${secId}`) || parsed.querySelector(secId);
            if (currentEl && newEl) {
              currentEl.innerHTML = newEl.innerHTML;
            }
          }
        });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: cartData }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cartData }));

      // The theme's own native pub/sub hook — the cart drawer's own "Goes
      // with this" upsell button already calls this, and it's almost
      // certainly what apps like Loop Returns listen for to know "the cart
      // changed, go re-check your auto-add item." Without this, Loop has no
      // signal that a pack was just added here at all.
      if (window.PUB_SUB_EVENTS && window.publish) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'custom-pack-builder', cartData });
      }
    }

    function openCartDrawer() {
      const cartDrawerEl = document.querySelector('cart-drawer') || document.querySelector('theme-drawer#cart-drawer') || document.querySelector('theme-drawer');
      if (cartDrawerEl) {
        cartDrawerEl.removeAttribute('hidden');
        cartDrawerEl.setAttribute('open', '');
        const dialog = cartDrawerEl.querySelector('dialog');
        if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
          dialog.showModal();
        }
      } else {
        const cartIcon = document.querySelector('.header-actions__cart-icon, [data-cart-drawer-toggle], .header__icon--cart, a[href="/cart"]');
        if (cartIcon) cartIcon.click();
      }
    }

    // Shopify's /cart/add.js can reject a multi-item request with a 422 —
    // e.g. "Only 3 items were added to your cart due to availability" — even
    // after it has *already* added whatever stock allowed straight into the
    // real cart. Confirmed live: sending 6 line items for a variant with only
    // 3 in stock returns a 422, but the cart ends up with 3 of that item in
    // it regardless. The old code only handled the success path, so on that
    // 422 the page was left showing 0 added and the shopper had to refresh
    // to see the 3 that were actually sitting in their bag.
    //
    // This asks Shopify for the *current* cart's section HTML (an empty
    // "updates" object changes nothing) and re-renders it, so the drawer
    // always reflects the truth without a page refresh.
    function resyncCartAfterFailedAdd() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates: {},
          sections: ['cart-drawer-section', 'cart-drawer', 'cart-icon-bubble']
        })
      })
        .then(res => res.json())
        .then(cartData => applyCartSections(cartData))
        .catch(err => console.error(err));
    }

    // Returns true stock left for a variant, accounting for the merchant's
    // "continue selling when out of stock" setting. When tracking is off,
    // or oversell is allowed, there's no real ceiling to enforce.
    function getRemainingStock(variant) {
      if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
        return typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : Infinity;
      }
      return Infinity;
    }

    // How many of the current bundle's slots are already holding this exact
    // variant. excludeSlot lets us ignore the slot we're currently replacing
    // (so re-picking the same item into the same slot doesn't count against
    // itself).
    function countSelectedForVariant(variantId, excludeSlot) {
      let count = 0;
      Object.keys(selections).forEach(function (k) {
        if (excludeSlot !== undefined && Number(k) === Number(excludeSlot)) return;
        if (selections[k] && String(selections[k].id) === String(variantId)) count++;
      });
      return count;
    }

    // Renders the product cards for whichever products were last fetched for
    // the active category, filtered against the current size selection
    // (standard currentSelectedSize, or currentSelectedWaist/Length for a
    // denim category). Pulled out of fetchProductsForDrawer so a waist or
    // length change can re-filter the already-fetched list without another
    // network round trip.
    function renderDrawerProductCards(products) {
      const container = document.getElementById('drawer-products-container');
      if (!container || !products) return;

      container.innerHTML = '';

      const isExactSizeMatch = (variantOption, targetSize) => {
        if (!variantOption || !targetSize) return false;
        return variantOption.toString().trim().toUpperCase() === targetSize.toString().trim().toUpperCase();
      };

      if (!isDenimCategory) {
        const availableSizesInCat = computeAvailableSizesInCat(products);
        document.querySelectorAll('#drawer-size-list-standard .drawer-size-btn').forEach(btn => {
          const btnSize = btn.getAttribute('data-size').trim().toUpperCase();
          if (availableSizesInCat.has(btnSize)) {
            btn.classList.remove('sold-out');
          } else {
            btn.classList.add('sold-out');
          }
        });
      }

      let visibleProductsCount = 0;

      products.forEach(product => {
        if (!isRealProduct(product)) return;

        let matchedVariant = null;

        if (isDenimCategory) {
          if (currentSelectedWaist) {
            for (let i = 0; i < product.variants.length; i++) {
              const m = denimVariantMatches(product.variants[i], currentSelectedWaist, currentSelectedLength);
              if (m) { matchedVariant = m; break; }
            }
            if (!matchedVariant || !matchedVariant.available) return;

            const remaining = getRemainingStock(matchedVariant);
            if (remaining !== Infinity) {
              const usedInBundle = countSelectedForVariant(matchedVariant.id, activeSlotIndex);
              if (usedInBundle >= remaining) return;
            }
          }
        } else if (currentSelectedSize) {
          matchedVariant = product.variants.find(v => {
            return isExactSizeMatch(v.option1, currentSelectedSize) ||
                   isExactSizeMatch(v.option2, currentSelectedSize) ||
                   isExactSizeMatch(v.option3, currentSelectedSize) ||
                   isExactSizeMatch(v.title, currentSelectedSize);
          });

          if (!matchedVariant || !matchedVariant.available) return;

          const remaining = getRemainingStock(matchedVariant);
          if (remaining !== Infinity) {
            const usedInBundle = countSelectedForVariant(matchedVariant.id, activeSlotIndex);
            if (usedInBundle >= remaining) return;
          }
        }

        visibleProductsCount++;

        const card = document.createElement('div');
        card.className = 'drawer-product-card';
        card.dataset.productId = product.id;

        // Full image list for this product (not just the first one), so the
        // card can offer prev/next arrows when there's more than one.
        const images = product.images || [];
        const imgSrc = images.length > 0 ? images[0].src : '';
        const hasMultipleImages = images.length > 1;

        // Show the pack-discounted price here (not the plain RRP) so the
        // saving is visible while browsing, matching the discount already
        // applied in the footer summary and the main add-to-cart button.
        // Uses the SAME matchedVariant (falls back to variants[0], same as
        // variantForCart below) and the SAME getDiscountRateFor/roundMoney
        // helpers as computePricing() — display only, no change to the
        // price stored in selections[] or sent to the cart.
        const cardVariant = matchedVariant ? matchedVariant : product.variants[0];
        const unitPrice = cardVariant ? Number(cardVariant.price || 0) : 0;
        const cardDiscountRate = getDiscountRateFor(quantity);
        const cardDiscountedPrice = cardDiscountRate > 0 ? discountedUnitPrice(unitPrice, cardDiscountRate) : unitPrice;
        const showCardCompare = cardDiscountRate > 0 && cardDiscountedPrice < unitPrice;

        const priceHtml = showCardCompare
          ? `<span class="drawer-price-compare">${fmt(unitPrice)}</span><span class="drawer-price-sale">${fmt(cardDiscountedPrice)}</span>`
          : `<span class="drawer-price-sale">${fmt(unitPrice)}</span>`;

        const arrowsHtml = hasMultipleImages
          ? `
            <button type="button" class="drawer-img-arrow drawer-img-arrow--prev" aria-label="Previous image">&lsaquo;</button>
            <button type="button" class="drawer-img-arrow drawer-img-arrow--next" aria-label="Next image">&rsaquo;</button>
          `
          : '';

        card.innerHTML = `
          <div class="drawer-product-image-wrap">
            <img src="${imgSrc}" alt="${product.title}">
            ${arrowsHtml}
          </div>
          <div class="bundle-wrapper">
          <div class="drawer-product-title">${product.title}</div>
          <div class="drawer-product-price">${priceHtml}</div>
          </div>
          <div class="bundle-button">
          <button type="button" class="drawer-add-btn">
            ADD TO BUNDLE
          </button>
          </div>
        `;

        if (hasMultipleImages) {
          const imgEl = card.querySelector('img');
          let imageIndex = 0;

          const showImage = (nextIndex) => {
            imageIndex = (nextIndex + images.length) % images.length;
            imgEl.src = images[imageIndex].src;
          };

          const prevBtn = card.querySelector('.drawer-img-arrow--prev');
          const nextBtn = card.querySelector('.drawer-img-arrow--next');

          if (prevBtn) {
            prevBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              showImage(imageIndex - 1);
            });
          }
          if (nextBtn) {
            nextBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              showImage(imageIndex + 1);
            });
          }
        }

        card.querySelector('.drawer-add-btn').addEventListener('click', function () {
          if (isDenimCategory) {
            if (!currentSelectedWaist || !currentSelectedLength) {
              alert('Please select a waist and length first!');
              return;
            }
          } else if (!currentSelectedSize) {
            alert('Please select a size first!');
            return;
          }

          let targetSlot = (activeSlotIndex !== null && activeSlotIndex >= 0 && activeSlotIndex < quantity)
            ? activeSlotIndex
            : findNextEmptySlot();

          if (targetSlot === -1) {
            alert('Bundle is full. Remove an item to add another.');
            return;
          }

          const variantForCart = matchedVariant ? matchedVariant : product.variants[0];

          // Final stock check right before committing the pick — covers the
          // case where this card was rendered before stock ran out (e.g.
          // another slot claimed it moments ago).
          const remaining = getRemainingStock(variantForCart);
          if (remaining !== Infinity) {
            const usedElsewhere = countSelectedForVariant(variantForCart.id, targetSlot);
            if (usedElsewhere >= remaining) {
              alert(`Only ${remaining} left in this size — you've already used ${usedElsewhere} in this bundle.`);
              renderDrawerProductCards(lastFetchedProducts);
              return;
            }
          }

          const sizeLabel = isDenimCategory
            ? (currentSelectedWaist + ' / ' + currentSelectedLength)
            : currentSelectedSize;

          selections[targetSlot] = {
            id: variantForCart.id,
            productId: product.id,
            title: product.title,
            image: imgSrc,
            size: sizeLabel,
            price: parseFloat(variantForCart.price || 0)
          };

          activeSlotIndex = null;
          renderSlots();
        });

        container.appendChild(card);
      });

      updateDrawerProductCardStates();

      if (visibleProductsCount === 0) {
        const msg = isDenimCategory
          ? (!currentSelectedWaist || !currentSelectedLength
              ? 'Please select a waist and length.'
              : 'No products available in this waist/length.')
          : 'No products available in this size.';
        container.innerHTML = `<p class="drawer-loading-text">${msg}</p>`;
      }
    }

    function fetchProductsForDrawer() {
      const container = document.getElementById('drawer-products-container');
      if (!container) return;

      if (!currentCategoryHandle) {
        container.innerHTML = '<p class="drawer-loading-text">Please select a category.</p>';
        return;
      }

      container.innerHTML = '<p class="drawer-loading-text">Loading products...</p>';

      // NOTE: this used to hit the public /collections/{handle}/products.json
      // feed, which reports a variant as "available" if it has stock at ANY
      // location, regardless of whether that location actually ships to the
      // shopper's country. That mismatch is what let people add items here
      // that then got rejected as "out of stock" at /cart/add.js time.
      //
      // /collections/{handle}?view=bundle-drawer instead renders through
      // templates/collection.bundle-drawer.liquid — a normal Liquid render
      // (with {% layout none %} so it returns bare JSON), which computes
      // `variant.available` the same way a real collection/product page
      // does (filtered to locations that can actually ship to this visitor).
      fetch(`/collections/${currentCategoryHandle}?view=bundle-drawer`)
        .then(response => response.json())
        .then(data => {
          const fit = getCurrentProductFit();
          lastFetchedProducts = (data.products || []).filter(function (p) {
            return productMatchesFit(p, fit);
          });

          const denim = detectDenimCategory(lastFetchedProducts);
          toggleDenimMode(denim);

          if (denim) {
            const sizeData = collectDenimSizeData(lastFetchedProducts);
            renderDenimSizeControls(sizeData);
            updateDenimSizeText();
          }

          if (lastFetchedProducts.length > 0) {
            renderDrawerProductCards(lastFetchedProducts);
          } else {
            container.innerHTML = '<p class="drawer-loading-text">No products found in this category.</p>';
          }
        })
        .catch(err => {
          console.error(err);
          container.innerHTML = '<p class="drawer-loading-text">Error loading products.</p>';
        });
    }

    // ---- Global (document-level) listeners ------------------------------
    // These respond to events that can come from anywhere on the page, not
    // just from this builder instance's own markup, so they're bound to
    // `document` rather than to `root`. Because initBuilder() can now run
    // again after a length switch (see the top of this file), each one is
    // stashed on window.__cpbGlobalListeners and its previous copy removed
    // first — otherwise re-running this function would leave two or more
    // live copies responding to the same event, each holding its own
    // `selections`/`quantity`/etc. and fighting over the same visible DOM.

    if (window.__cpbGlobalListeners.drawerClick) {
      document.removeEventListener('click', window.__cpbGlobalListeners.drawerClick);
    }
    window.__cpbGlobalListeners.drawerClick = function (e) {
      if (e.target.classList.contains('drawer-size-btn')) {
        document.querySelectorAll('.drawer-size-btn').forEach(btn => btn.classList.remove('active'));
        e.target.classList.add('active');

        currentSelectedSize = e.target.getAttribute('data-size');
        const sizeTextEl = document.getElementById('drawer-selected-size-text');
        if (sizeTextEl) sizeTextEl.textContent = currentSelectedSize + ' SELECTED';

        currentCategoryHandle = '';
        document.querySelectorAll('.drawer-cat-card').forEach(card => card.classList.remove('active'));

        const catTextEl = document.getElementById('drawer-selected-cat-text');
        if (catTextEl) catTextEl.textContent = 'Select Category';

        const container = document.getElementById('drawer-products-container');
        if (container) {
          container.innerHTML = '<p class="drawer-loading-text">Please select a category.</p>';
        }
      }

      const catCard = e.target.closest('.drawer-cat-card');
      if (catCard) {
        document.querySelectorAll('.drawer-cat-card').forEach(card => card.classList.remove('active'));
        catCard.classList.add('active');
        currentCategoryHandle = catCard.getAttribute('data-handle');

        const catTextEl = document.getElementById('drawer-selected-cat-text');
        if (catTextEl) catTextEl.textContent = catCard.getAttribute('data-title');

        fetchProductsForDrawer();
      }
    };
    document.addEventListener('click', window.__cpbGlobalListeners.drawerClick);

    const closeBtn = document.getElementById('close-bundle-drawer');
    const overlay = document.getElementById('bundle-drawer-overlay');
    if (closeBtn) closeBtn.addEventListener('click', closeBundleDrawer);
    if (overlay) overlay.addEventListener('click', closeBundleDrawer);

    if (window.__cpbGlobalListeners.packChange) {
      document.removeEventListener('pack:change', window.__cpbGlobalListeners.packChange);
    }
    window.__cpbGlobalListeners.packChange = function (event) {
      let q = event.detail ? (event.detail.quantity || event.detail.packKey) : null;
      let rawKey = event.detail ? event.detail.packKey : null;

      if (typeof q === 'string') {
        const match = q.match(/\d+/);
        if (match) q = match[0];
      }

      if (rawKey) {
        currentPackKey = String(rawKey);
      } else if (q) {
        currentPackKey = String(q);
      }

      if (q) setQuantity(q);

      stepperQuantity = null;

      // setQuantity() above already re-renders and now preserves existing
      // picks rather than wiping them — this used to re-wipe selections and
      // re-render a second time right after, which undid that. Kept as a
      // plain renderSlots() (no reset) so rendering still happens even in
      // the rare case setQuantity() bails out early (e.g. a non-numeric
      // packKey with no quantity).
      renderSlots();

      updatePricing();
    };
    document.addEventListener('pack:change', window.__cpbGlobalListeners.packChange);

    if (window.__cpbGlobalListeners.colorPackChange) {
      document.removeEventListener('color-pack:change', window.__cpbGlobalListeners.colorPackChange);
    }
    window.__cpbGlobalListeners.colorPackChange = function (event) {
      if (event.detail && event.detail.swatchType === 'custom_pack') {
        currentPackKey = 'custom_pack';
        root.hidden = false;
        root.style.display = 'block';
        if (event.detail.quantity) {
          setQuantity(event.detail.quantity);
        } else {
          renderSlots();
        }
      } else {
        root.hidden = true;
        root.style.display = 'none';
        currentPackKey = detectCurrentPackKey();
      }

      updatePricing();
    };
    document.addEventListener('color-pack:change', window.__cpbGlobalListeners.colorPackChange);

    document.querySelectorAll('input[name="pack"], input[name="Pack"], .pack-swatch').forEach(input => {
      input.addEventListener('change', function () {
        const val = this.value || this.dataset.value;
        const newCategoryHandle = this.dataset.collectionHandle || this.dataset.handle;
        if (newCategoryHandle) {
          currentCategoryHandle = newCategoryHandle;
        }

        if (val) {
          const match = val.match(/\d+/);
          if (match) {
            setQuantity(match[0]);
            currentPackKey = match[0];
            updatePricing();
          }
        }
      });
    });

    const atcButtons = getMainAtcButtons();

    atcButtons.forEach(btn => {
      btn.addEventListener('click', function (e) {
        if (!window.currentProductData || !window.currentProductData.id) {
          return;
        }

        const stepperMismatch =
          currentPackKey !== 'singles' &&
          stepperQuantity !== null &&
          Number(stepperQuantity) < Number(quantity);

        if (stepperMismatch) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          alert(`This is a ${quantity}-Pack — please set quantity to at least ${quantity}, or choose a different pack.`);
          return false;
        }

        // Belt-and-braces alongside the disabled button state set in
        // updateCount(): a Build Your Own Bundle with empty slots must never
        // reach the cart-building loop below, because that loop fills any
        // empty slot with another of the base product — so an incomplete
        // bundle would otherwise still add at the bundle's full item count,
        // just duplicating whatever's currently selected instead of what the
        // shopper actually chose.
        const filledSlotCount = Object.keys(selections).length;
        if (!root.hidden && filledSlotCount < quantity) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          alert(`Please choose ${quantity - filledSlotCount} more item${quantity - filledSlotCount === 1 ? '' : 's'} for your bundle before adding it to your bag.`);
          return false;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const effectiveQuantity = getEffectiveQuantity();
        const itemsToAddToCart = [];
        for (let i = 0; i < effectiveQuantity; i++) {
          const item = selections[i] || {
            id: window.currentProductData.id,
            size: window.currentProductData.size || currentSelectedSize || ''
          };
          const variantId = parseInt(item.id, 10);

          if (!isNaN(variantId)) {
            itemsToAddToCart.push({
              id: variantId,
              quantity: 1,
              properties: {
                'Pack Size': `${quantity}-Pack Custom Bundle`,
                'Size': item.size || ''
              }
            });
          }
        }

        if (itemsToAddToCart.length === 0) return;

        const btnText = btn.querySelector('.add-to-cart-text__content span span') || btn.querySelector('.add-to-cart-text') || btn;
        const originalText = btnText.textContent;
        btnText.textContent = 'Adding to Bag...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: itemsToAddToCart,
            sections: ['cart-drawer-section', 'cart-drawer', 'cart-icon-bubble']
          })
        })
        .then(async (res) => {
          const cartData = await res.json();
          if (!res.ok) throw new Error(cartData.description || 'Error adding to cart');
          return cartData;
        })
        .then(cartData => {
          btnText.textContent = 'Added!';
          applyCartSections(cartData);
          setTimeout(openCartDrawer, 150);
        })
        .catch(err => {
          console.error(err);

          // Don't just alert Shopify's raw error text and leave the page
          // stale — it reads like a success message ("Only 3 items were
          // added...") even though res.ok was false, and the old code never
          // touched the cart drawer on this path, so shoppers had to refresh
          // to see what had actually landed in their bag. Tell them plainly
          // what happened instead, reset this bundle back to empty (Shopify's
          // response doesn't say which of the selected items made it in, so
          // restarting the bundle is the only reliable way to stop it thinking
          // sold-out items are still selected/available), then pull down the
          // real cart and open it so they can see exactly what's in it before
          // checking out.
          alert('Sorry, something stopped part of this bundle from being added to your bag. We’ve refreshed your bag below so you can check what’s in it before checking out.');
          selections = {};
          renderSlots();
          resyncCartAfterFailedAdd().then(openCartDrawer);
        })
        .finally(() => {
          setTimeout(() => {
            btnText.textContent = originalText;
          }, 1500);
        });

      }, true);
    });

    if (window.__cpbGlobalListeners.quantityUpdate) {
      document.removeEventListener('quantity-selector:update', window.__cpbGlobalListeners.quantityUpdate);
    }
    window.__cpbGlobalListeners.quantityUpdate = function (event) {
      if (!event.detail) return;
      if (event.detail.cartLine !== undefined) return;

      stepperQuantity = Number(event.detail.quantity);
      updateCount();
    };
    document.addEventListener('quantity-selector:update', window.__cpbGlobalListeners.quantityUpdate);

    autoSelectFirstProduct();
    renderSlots();

    currentPackKey = detectCurrentPackKey();
    observePresetGroups();
    updatePricing();

    const bdfCta = queryOwn('[data-bdf-cta]');
    if (bdfCta && !bdfCta.__bdfBound) {
      bdfCta.__bdfBound = true;
      bdfCta.addEventListener('click', function () {
        if (bdfCta.disabled) return;

        bdfCta.classList.add('is-loading');
        const originalText = bdfCta.textContent;
        bdfCta.textContent = 'ADDING…';

        const mainAtc = getMainAtcButtons()[0];
        if (mainAtc) {
          mainAtc.click();
        }

        const closeAndReset = function () {
          closeBundleDrawer();
          setTimeout(function () {
            bdfCta.classList.remove('is-loading');
            bdfCta.textContent = originalText;
          }, 300);
        };
        document.addEventListener('cart:updated', closeAndReset, { once: true });
        setTimeout(function () {
          bdfCta.classList.remove('is-loading');
          if (bdfCta.textContent === 'ADDING…') bdfCta.textContent = originalText;
        }, 4000);
      });
    }

    // ---- Default selection so the drawer isn't empty on first open --------
    // Mirrors the exact approach already shipped on the standalone Bundle
    // Builder Page (bundle-builder-page.js): fire a real click on a default
    // size button and the first category pill, so the SAME click handlers
    // wired up above run exactly as if a shopper had clicked them themselves
    // — nothing new to keep in sync with. Order matters: clicking a size
    // button resets the category selection (see the .drawer-size-btn handler
    // above), so size is clicked first, category second — same order the
    // page uses, for the same reason.
    const defaultSizeBtn = document.querySelector('#drawer-size-list-standard .drawer-size-btn[data-size="L"]');
    if (defaultSizeBtn) defaultSizeBtn.click();

    const defaultCatCard = document.querySelector('.drawer-cat-card');
    if (defaultCatCard) defaultCatCard.click();
  }
})();
